import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { supabase } from '../supabase';
import { getUser } from '../auth';
import Confettis from '../components/Confettis';

export default function Notation() {
  const navigate = useNavigate();
  const { id } = useParams();
  const user = getUser();
  const [activity, setActivity] = useState(null);
  const [note, setNote] = useState(0);
  const [commentaire, setCommentaire] = useState('');
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [confettis, setConfettis] = useState(false);
  const [step, setStep] = useState(1); // 1: photos, 2: note
  const fileRef = useRef();

  useEffect(() => {
    if (!user) { navigate('/connexion'); return; }
    fetchActivity();
  }, []);

  const fetchActivity = async () => {
    const { data } = await supabase.from('activites').select('*, users(prenom)').eq('id', id).single();
    if (data) setActivity(data);
  };

  const handlePhotos = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setUploading(true);
    const uploaded = [];
    for (const file of files) {
      try {
        const ext = file.name.split('.').pop();
        const path = `chat/${id}/${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;
        await supabase.storage.from('photos').upload(path, file, { upsert: true });
        const { data } = supabase.storage.from('photos').getPublicUrl(path);
        uploaded.push(data.publicUrl);
        // Poster dans le chat de l'activité
        await supabase.from('messages').insert({
          activite_id: id,
          user_id: user.id,
          texte: '',
          pseudo: user.prenom,
          image_url: data.publicUrl,
        });
      } catch (err) { console.error(err); }
    }
    setPhotos(prev => [...prev, ...uploaded]);

    // Notifier les participants
    if (uploaded.length > 0) {
      const { data: participants } = await supabase
        .from('participations')
        .select('user_id')
        .eq('activite_id', id)
        .neq('user_id', user.id);

      if (participants) {
        for (const p of participants) {
          await supabase.from('notifications').insert({
            user_id: p.user_id,
            type: 'photo',
            message: `${user.prenom} a posté des photos de "${activity?.titre}" 📸 Viens les voir !`,
            activite_id: id,
            lu: false,
          });
        }
      }
    }
    setUploading(false);
  };

  const handleSubmit = async () => {
    if (!note) { alert('Donne une note à cette sortie !'); return; }
    setLoading(true);
    await supabase.from('notations').insert({
      activite_id: id,
      user_id: user.id,
      note,
      commentaire: commentaire.trim(),
    });
    setLoading(false);
    setConfettis(true);
    setTimeout(() => { setConfettis(false); setDone(true); }, 1500);
  };

  if (done) return (
    <div style={{ minHeight: '100vh', background: '#FAFAF7', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '40px 24px', textAlign: 'center' }}>
      <Confettis trigger={true} onDone={() => {}} />
      <div style={{ fontSize: '4rem', marginBottom: 16, animation: 'pop 0.5s ease' }}>🎉</div>
      <div style={{ fontFamily: 'Fredoka One, cursive', fontSize: '1.8rem', color: '#1C2B1E', marginBottom: 8 }}>Merci !</div>
      <div style={{ fontSize: '0.9rem', color: '#8A9E8D', lineHeight: 1.6, marginBottom: 32 }}>
        {photos.length > 0
          ? `Tes ${photos.length} photo${photos.length > 1 ? 's' : ''} sont visibles dans le chat 📸`
          : 'Ta note a bien été envoyée !'}
      </div>
      <button onClick={() => navigate(`/detail/${id}`)} style={{ width: '100%', maxWidth: 320, padding: '16px', background: '#3D9A6A', color: 'white', border: 'none', borderRadius: 100, fontFamily: 'Nunito, sans-serif', fontWeight: 900, fontSize: '1rem', cursor: 'pointer', marginBottom: 12, boxShadow: '0 6px 20px rgba(61,154,106,0.35)' }}>
        💬 Voir le chat et les photos
      </button>
      <button onClick={() => navigate('/feed')} style={{ width: '100%', maxWidth: 320, padding: '14px', background: 'transparent', color: '#8A9E8D', border: '2px solid #E8EFE9', borderRadius: 100, fontFamily: 'Nunito, sans-serif', fontWeight: 700, fontSize: '0.9rem', cursor: 'pointer' }}>
        Retour aux activités
      </button>
      <style>{`@keyframes pop{0%{transform:scale(0)}70%{transform:scale(1.2)}100%{transform:scale(1)}}`}</style>
    </div>
  );

  return (
    <div style={{ minHeight: '100vh', background: '#FAFAF7', paddingBottom: 40 }}>
      <Confettis trigger={confettis} onDone={() => setConfettis(false)} />

      {/* HERO */}
      <div style={{ background: 'linear-gradient(160deg, #1C2B1E, #2D7A52)', padding: '48px 24px 40px', textAlign: 'center', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', width: 200, height: 200, borderRadius: '50%', background: 'radial-gradient(circle, #52C289, transparent)', opacity: 0.2, top: -60, right: -40 }} />

        <div style={{ fontSize: '3.5rem', marginBottom: 12 }}>🌿</div>
        <div style={{ fontFamily: 'Fredoka One, cursive', fontSize: '1.6rem', color: 'white', marginBottom: 8 }}>
          La sortie est terminée !
        </div>
        <div style={{ fontSize: '0.88rem', color: 'rgba(255,255,255,0.75)', lineHeight: 1.6 }}>
          {activity?.titre && `"${activity.titre}"`}
          <br />Partage les souvenirs avec les autres familles 📸
        </div>

        {/* PROGRESS */}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginTop: 24 }}>
          {[1, 2].map(s => (
            <div key={s} style={{ height: 4, width: step >= s ? 40 : 20, borderRadius: 100, background: step >= s ? '#52C289' : 'rgba(255,255,255,0.3)', transition: 'all 0.3s ease' }} />
          ))}
        </div>
      </div>

      <div style={{ padding: '24px' }}>

        {/* STEP 1 — PHOTOS */}
        {step === 1 && (
          <div>
            <div style={{ textAlign: 'center', marginBottom: 24 }}>
              <div style={{ fontFamily: 'Fredoka One, cursive', fontSize: '1.3rem', color: '#1C2B1E', marginBottom: 6 }}>
                📸 Partage tes photos !
              </div>
              <div style={{ fontSize: '0.85rem', color: '#8A9E8D', lineHeight: 1.6 }}>
                Les autres familles pourront les voir dans le chat. Ils reviendront les regarder !
              </div>
            </div>

            {/* ZONE UPLOAD */}
            <div onClick={() => fileRef.current?.click()} style={{ border: '2.5px dashed #C5DFC9', borderRadius: 24, padding: '32px 24px', textAlign: 'center', cursor: 'pointer', background: 'white', marginBottom: 16, position: 'relative' }}>
              {uploading ? (
                <div>
                  <div style={{ fontSize: '2rem', marginBottom: 8 }}>⏳</div>
                  <div style={{ fontFamily: 'Fredoka One, cursive', color: '#3D9A6A' }}>Upload en cours...</div>
                </div>
              ) : (
                <div>
                  <div style={{ fontSize: '3rem', marginBottom: 12 }}>📸</div>
                  <div style={{ fontFamily: 'Fredoka One, cursive', fontSize: '1.1rem', color: '#3D9A6A', marginBottom: 6 }}>Ajouter des photos</div>
                  <div style={{ fontSize: '0.78rem', color: '#8A9E8D' }}>Appuie pour choisir depuis ta galerie</div>
                </div>
              )}
              <input ref={fileRef} type="file" accept="image/*" multiple style={{ display: 'none' }} onChange={handlePhotos} />
            </div>

            {/* PREVIEW PHOTOS */}
            {photos.length > 0 && (
              <div style={{ marginBottom: 20 }}>
                <div style={{ fontSize: '0.78rem', fontWeight: 800, color: '#4A6355', marginBottom: 10, textTransform: 'uppercase', letterSpacing: 1 }}>
                  {photos.length} photo{photos.length > 1 ? 's' : ''} partagée{photos.length > 1 ? 's' : ''} ✅
                </div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {photos.map((url, i) => (
                    <img key={i} src={url} alt="" style={{ width: 80, height: 80, borderRadius: 16, objectFit: 'cover', boxShadow: '0 4px 12px rgba(28,43,30,0.12)' }} />
                  ))}
                </div>
              </div>
            )}

            <button onClick={() => setStep(2)} style={{ width: '100%', padding: '17px', background: photos.length > 0 ? '#3D9A6A' : 'rgba(61,154,106,0.5)', color: 'white', border: 'none', borderRadius: 100, fontFamily: 'Nunito, sans-serif', fontWeight: 900, fontSize: '1rem', cursor: 'pointer', marginBottom: 12, boxShadow: photos.length > 0 ? '0 6px 20px rgba(61,154,106,0.35)' : 'none' }}>
              {photos.length > 0 ? `Continuer avec ${photos.length} photo${photos.length > 1 ? 's' : ''} →` : 'Continuer sans photo →'}
            </button>

            <button onClick={() => navigate('/feed')} style={{ width: '100%', padding: '13px', background: 'transparent', color: '#8A9E8D', border: 'none', fontFamily: 'Nunito, sans-serif', fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer' }}>
              Passer
            </button>
          </div>
        )}

        {/* STEP 2 — NOTE */}
        {step === 2 && (
          <div>
            <div style={{ textAlign: 'center', marginBottom: 28 }}>
              <div style={{ fontFamily: 'Fredoka One, cursive', fontSize: '1.3rem', color: '#1C2B1E', marginBottom: 6 }}>
                ⭐ Note cette sortie !
              </div>
              <div style={{ fontSize: '0.85rem', color: '#8A9E8D', lineHeight: 1.6 }}>
                Ta note aide les autres familles à choisir les meilleures sorties.
              </div>
            </div>

            {/* ÉTOILES */}
            <div style={{ background: 'white', borderRadius: 24, padding: '24px', boxShadow: '0 4px 20px rgba(28,43,30,0.07)', marginBottom: 16, textAlign: 'center' }}>
              <div style={{ display: 'flex', justifyContent: 'center', gap: 12, marginBottom: 12 }}>
                {[1, 2, 3, 4, 5].map(i => (
                  <div key={i} onClick={() => setNote(i)} style={{ fontSize: note >= i ? '2.5rem' : '2rem', cursor: 'pointer', transition: 'all 0.2s', filter: note >= i ? 'none' : 'grayscale(1) opacity(0.4)', transform: note >= i ? 'scale(1.1)' : 'scale(1)' }}>
                    ⭐
                  </div>
                ))}
              </div>
              <div style={{ fontFamily: 'Fredoka One, cursive', fontSize: '1rem', color: note > 0 ? '#3D9A6A' : '#8A9E8D' }}>
                {note === 0 ? 'Appuie sur une étoile' : note === 1 ? 'Pas génial...' : note === 2 ? 'Peut mieux faire' : note === 3 ? 'Sympa !' : note === 4 ? 'Super sortie !' : 'Parfait ! 🎉'}
              </div>
            </div>

            {/* COMMENTAIRE */}
            <div style={{ background: 'white', borderRadius: 24, padding: '16px', boxShadow: '0 4px 20px rgba(28,43,30,0.07)', marginBottom: 20 }}>
              <div style={{ fontSize: '0.78rem', fontWeight: 800, color: '#4A6355', marginBottom: 10, textTransform: 'uppercase', letterSpacing: 1 }}>
                Un mot sur cette sortie ? (optionnel)
              </div>
              <textarea
                value={commentaire}
                onChange={e => setCommentaire(e.target.value)}
                placeholder="Super ambiance, les enfants ont adoré ! On recommande..."
                style={{ width: '100%', height: 100, padding: '12px', border: '2px solid #E8EFE9', borderRadius: 16, fontFamily: 'Nunito, sans-serif', fontSize: '0.9rem', outline: 'none', resize: 'none', background: '#FAFAF7', color: '#1C2B1E', boxSizing: 'border-box' }}
              />
            </div>

            {photos.length > 0 && (
              <div style={{ background: '#EDF7F1', borderRadius: 16, padding: '12px 16px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontSize: '1.2rem' }}>📸</span>
                <div style={{ fontSize: '0.82rem', color: '#4A6355', fontWeight: 600 }}>
                  {photos.length} photo{photos.length > 1 ? 's' : ''} déjà partagée{photos.length > 1 ? 's' : ''} dans le chat !
                </div>
              </div>
            )}

            <button onClick={handleSubmit} disabled={loading || !note} style={{ width: '100%', padding: '17px', background: note > 0 ? '#3D9A6A' : '#E8EFE9', color: note > 0 ? 'white' : '#8A9E8D', border: 'none', borderRadius: 100, fontFamily: 'Nunito, sans-serif', fontWeight: 900, fontSize: '1rem', cursor: note > 0 ? 'pointer' : 'not-allowed', boxShadow: note > 0 ? '0 6px 20px rgba(61,154,106,0.35)' : 'none', opacity: loading ? 0.7 : 1, marginBottom: 12 }}>
              {loading ? 'Envoi...' : '⭐ Envoyer ma note'}
            </button>

            <button onClick={() => navigate('/feed')} style={{ width: '100%', padding: '13px', background: 'transparent', color: '#8A9E8D', border: 'none', fontFamily: 'Nunito, sans-serif', fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer' }}>
              Passer
            </button>
          </div>
        )}
      </div>
    </div>
  );
}